import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import useSeo from '@/hooks/useSeo';
import { api } from '@/lib/api';

function formatApiErrorDetail(detail) {
    if (detail == null) return 'Something went wrong. Please try again.';
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) return detail.map((e) => (e && typeof e.msg === 'string' ? e.msg : '')).filter(Boolean).join(' ') || 'Invalid input.';
    if (detail && typeof detail.msg === 'string') return detail.msg;
    return String(detail);
}

export default function SignInPage() {
    const { user, signInGoogle, refresh } = useAuth();
    useSeo({ title: 'Sign In', description: 'Sign in to your S.M.I. account.', path: '/signin', noindex: true });
    const navigate = useNavigate();
    const [mode, setMode] = useState('login');
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [error, setError] = useState('');
    const [busy, setBusy] = useState(false);
    const [forgotSent, setForgotSent] = useState(false);

    useEffect(() => {
        if (user) navigate('/desk', { replace: true });
    }, [user, navigate]);

    if (user) return null;

    const submit = async (e) => {
        e.preventDefault();
        if (busy) return;
        setBusy(true);
        setError('');
        try {
            if (mode === 'forgot') {
                await api.post('/auth/forgot-password', { email });
                setForgotSent(true);
            } else {
                const path = mode === 'login' ? '/auth/login' : '/auth/register';
                const payload = mode === 'login' ? { email, password } : { email, password, name };
                await api.post(path, payload, { withCredentials: true });
                await refresh();
                navigate('/desk', { replace: true });
            }
        } catch (err) {
            setError(formatApiErrorDetail(err.response?.data?.detail) || err.message);
        }
        setBusy(false);
    };

    const inputCls = 'w-full rounded-lg border border-white/10 bg-black/40 px-4 py-3 text-sm text-white placeholder:text-lab-dim focus:border-lab-cyan/60 focus:outline-none';

    return (
        <div className="min-h-screen flex items-center justify-center px-6 py-24" data-testid="signin-page">
            <div className="glass rounded-2xl border border-white/10 w-full max-w-md p-8">
                <div className="lab-mono text-[10px] tracking-[0.3em] text-lab-mute mb-2">FACILITY ACCESS</div>
                <h1 className="font-display text-3xl text-white mb-6">
                    {mode === 'login' ? 'Sign in to S.M.I.' : mode === 'register' ? 'Create your account' : 'Reset your password'}
                </h1>

                <button
                    type="button"
                    onClick={signInGoogle}
                    data-testid="signin-google-btn"
                    className="w-full rounded-full border border-white/20 bg-white/5 px-4 py-3 text-sm text-white hover:bg-white/10 transition-colors"
                >
                    Continue with Google
                </button>

                <div className="flex items-center gap-3 my-6">
                    <div className="h-px flex-1 bg-white/10" />
                    <span className="lab-mono text-[10px] text-lab-dim">OR USE EMAIL</span>
                    <div className="h-px flex-1 bg-white/10" />
                </div>

                <form onSubmit={submit} className="space-y-3">
                    {mode === 'forgot' && forgotSent && (
                        <div className="text-xs text-lab-emerald leading-relaxed" data-testid="signin-forgot-sent">
                            If an account exists for that email, a reset link is on its way. Check your inbox.
                        </div>
                    )}
                    {mode === 'register' && (
                        <input
                            type="text" value={name} onChange={(e) => setName(e.target.value)}
                            placeholder="Name (optional)" maxLength={80}
                            data-testid="signin-name-input" className={inputCls}
                        />
                    )}
                    <input
                        type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                        placeholder="you@example.com" autoComplete="email"
                        data-testid="signin-email-input" className={inputCls}
                    />
                    {mode !== 'forgot' && (
                        <input
                            type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                            placeholder={mode === 'register' ? 'Password (min 8 characters)' : 'Password'}
                            minLength={mode === 'register' ? 8 : 1} autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
                            data-testid="signin-password-input" className={inputCls}
                        />
                    )}
                    {error && (
                        <div className="text-xs text-red-400 leading-relaxed" data-testid="signin-error">{error}</div>
                    )}
                    <button
                        type="submit" disabled={busy}
                        data-testid="signin-submit-btn"
                        className="w-full rounded-full bg-gradient-to-r from-lab-cyan to-lab-violet px-4 py-3 text-sm font-medium text-black hover:opacity-90 transition-opacity disabled:opacity-50"
                    >
                        {busy ? 'Working…' : mode === 'login' ? 'Sign in' : mode === 'register' ? 'Create account' : 'Send reset link'}
                    </button>
                </form>

                {mode === 'login' && (
                    <button
                        type="button"
                        onClick={() => { setMode('forgot'); setError(''); setForgotSent(false); }}
                        data-testid="signin-forgot-link"
                        className="mt-3 w-full text-center text-xs text-lab-dim hover:text-white transition-colors"
                    >
                        Forgot password?
                    </button>
                )}

                <button
                    type="button"
                    onClick={() => { setMode(mode === 'login' ? 'register' : 'login'); setError(''); setForgotSent(false); }}
                    data-testid="signin-mode-toggle"
                    className="mt-5 w-full text-center text-xs text-lab-mute hover:text-white transition-colors"
                >
                    {mode === 'login' ? 'No account? Create one with email' : mode === 'register' ? 'Already have an account? Sign in' : 'Back to sign in'}
                </button>
            </div>
        </div>
    );
}
